import { inject, Injectable } from '@angular/core';
import { forkJoin, map, Observable, of, switchMap, take } from 'rxjs';
import type Stripe from 'stripe';
import { expiresSoon, isExpired } from '../lib/card-expired';
import { isEnterprise, isFreeSubscription } from '../lib/subscription-helpers';
import { CardService } from './card.service';
import { StateService } from './state.service';
import { SubscriptionService } from './subscription.service';

@Injectable({
  providedIn: 'root',
})
export class NonPaymentService {
  private state = inject(StateService);
  private cardService = inject(CardService);
  private subscriptionService = inject(SubscriptionService);

  public getStatus(): Observable<'expiresSoon' | 'expired' | 'couldntCharge' | 'enterpriseOverdue'> {
    return forkJoin([
      this.cardService.getCardData().pipe(take(1)),
      this.subscriptionService.getMainPrice().pipe(take(1)),
      this.subscriptionService.getSubscription().pipe(take(1)),
    ]).pipe(
      map(([cardData, mainPrice, subscription]) => {
        if (!cardData.card) return;
        if (this.state.temporarilyHideUpgradeCta) return;
        if (!subscription) return;
        if (isFreeSubscription(subscription)) return;
        if (!['admin', 'owner'].includes(this.state.activeTenantRole)) return;

        if (isEnterprise(mainPrice)) {
          if (['unpaid', 'past_due'].includes(subscription.status)) return 'enterpriseOverdue';
          return;
        }

        if (['unpaid', 'past_due'].includes(subscription.status)) {
          return 'couldntCharge';
        }

        if (subscription.status === 'active') {
          if (isExpired(cardData.card)) return 'expired';
          if (expiresSoon(cardData.card)) return 'expiresSoon';
        }
      })
    );
  }

  public isBlocked(): Observable<boolean> {
    return forkJoin([
      this.subscriptionService.getSubscription().pipe(take(1)),
      this.subscriptionService.getMainPrice().pipe(take(1)),
    ]).pipe(
      switchMap(([sub, mainPrice]) => {
        if (isFreeSubscription(sub)) return of(false);
        if (isEnterprise(mainPrice)) return of(false);
        return this.getStatus().pipe(
          map(status => {
            if (status !== 'couldntCharge') return false;
            const invoice = sub.latest_invoice as Stripe.Invoice;
            if (!invoice) return false;
            const endDate = invoice.period_end * 1000;
            const daysSinceEnd = (new Date().getTime() - endDate) / (1000 * 60 * 60 * 24);
            return daysSinceEnd > 4;
          })
        );
      })
    );
  }
}
